import { LS_ANON_DEVICE_ID, LS_ANON_RATINGS } from "../../constants/SessionKeys";

/**
 * Identity for logged-out business ratings.
 *
 * - A random device UUID is generated once and kept in localStorage; it is sent as X-Device-Id so the
 *   backend can hold one anonymous rating per (business, device).
 * - The anonymous rating this browser posted for each business is remembered by ratingId, because the
 *   API deliberately returns anonymous ratings with no rater id -- this map is how the UI knows which
 *   row is "yours" (to show edit/delete).
 *
 * localStorage can be unavailable or throw (private mode, blocked site data), so every access is wrapped
 * and falls back to in-memory values that last for the page session.
 */

let memoryDeviceId: string | null = null;
let memoryRatings: Record<string, string> = {};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function generateUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for older webviews without crypto.randomUUID.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function readRatingsMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(LS_ANON_RATINGS);
    if (!raw) return memoryRatings;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : memoryRatings;
  } catch {
    return memoryRatings;
  }
}

function writeRatingsMap(map: Record<string, string>): void {
  memoryRatings = map;
  try {
    localStorage.setItem(LS_ANON_RATINGS, JSON.stringify(map));
  } catch {
    /* storage unavailable: the in-memory copy still works for this page session */
  }
}

const AnonymousRatingHelper = {
  /** Stable per-browser device UUID (created on first use). */
  getDeviceId(): string {
    try {
      const stored = localStorage.getItem(LS_ANON_DEVICE_ID);
      if (stored && UUID_RE.test(stored)) return stored;
      const fresh = memoryDeviceId ?? generateUuid();
      memoryDeviceId = fresh;
      localStorage.setItem(LS_ANON_DEVICE_ID, fresh);
      return fresh;
    } catch {
      memoryDeviceId = memoryDeviceId ?? generateUuid();
      return memoryDeviceId;
    }
  },

  /** ratingId of the anonymous rating this browser posted for the business, if any. */
  getOwnRatingId(businessId: string): string | null {
    return readRatingsMap()[businessId] ?? null;
  },

  rememberOwnRating(businessId: string, ratingId: string): void {
    writeRatingsMap({ ...readRatingsMap(), [businessId]: ratingId });
  },

  forgetOwnRating(businessId: string): void {
    const { [businessId]: _removed, ...rest } = readRatingsMap();
    writeRatingsMap(rest);
  },
};

export default AnonymousRatingHelper;
